import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core'
import {
  EditableRow,
  EntityBase,
  Entity,
  EntityRelationship,
  EntityTypeCode,
  EntityTypeMetadata,
  RelationshipType,
  EditableRowField,
  ChildEntityCandidate,
  effectiveDateColumns,
  formatEntity,
  getDetailsFieldLabel,
} from './entity-metadata'
import { v4 as uuidV4 } from 'uuid'
import { EntityManagementService } from './entity-management.service'
import { Option } from '../shared/models/general'
import { EditableTableComponent, EditorSpec } from './editable-table.component'
import { ColDef } from 'ag-grid-community'
import { auditColumns } from './entity-metadata'
import { dateOnlyFormatter, getOptionsFormatter } from '../shared/utilities/formatters'
import { SelectCellEditorComponent, SelectCellEditorParams } from './select-cell-editor.component'
import { validateRelationships } from './validation'
import { map, Observable } from 'rxjs'
import { sortBy } from 'lodash'
import { ChangeSetDescriptor } from './entity-management-changes.service'


const getId = (row: EditableRow<EntityRelationship>): string =>
  row.entity_relationship_uid!

type ChildEntityOption = Option & Readonly<{
  termination_date?: string
}>

const toOption = (entity: EntityBase): Option => ({
  value: entity.entity_uid,
  display: formatEntity(entity),
})

const sortedOptions = (options: readonly Option[]): readonly Option[] =>
  sortBy(options, 'display')

const toChildEntityOption = (
  candidate: ChildEntityCandidate,
): ChildEntityOption =>
  candidate.termination_date
    ? ({ ...toOption(candidate), termination_date: candidate.termination_date })
    : toOption(candidate)

@Component({
  selector: 'app-entity-relationships-editor',
  imports: [
    EditableTableComponent,
  ],
  template: `
    <app-editable-table
        [changeSetDescriptor]="changeSetDescriptor()"
        [spec]="editorSpec()"
        [groupBy]="groupBy()"
        [maxNewRows]="maxNewRows()"
        [rows]="entityRelationships()"
        (saveComplete)="saveComplete.emit(entityTypeMetadata().entity_type_code)"
        (error)="error.emit($event)"
    />
  `,
})
export class EntityRelationshipsEditorComponent {
  private readonly entityManagementService = inject(EntityManagementService)

  readonly relationshipType = input.required<RelationshipType>()
  readonly entity = input.required<Entity>()
  readonly url = input.required<string>()
  readonly entityTypeMetadata = input.required<EntityTypeMetadata>()
  readonly entityRelationships = input.required<readonly EntityRelationship[]>()

  readonly saveComplete = output<EntityTypeCode>()
  readonly error = output<Error>()

  protected readonly relatedEntityOptions = signal<readonly Option[]>([])

  protected relatedEntityOptionsLookup = computed((): Readonly<Record<string, Option>> => {
    const lookup: Record<string, Option> = {}
    this.relatedEntityOptions().forEach(o => {
      lookup[o.value] = o
    })
    return lookup
  })

  protected readonly changeSetDescriptor = computed((): ChangeSetDescriptor => {
    const { entity_type_code, entity_code } = this.entity()
    const entityDesc = `${entity_type_code} ${entity_code}`
    const meta = this.entityTypeMetadata()
    return ({
      url: this.url(),
      description: this.relationshipType() === 'parent' ? `${meta.entity_type_code} -> ${entityDesc}` : `${entityDesc} -> ${meta.entity_type_code}s`,
    })
  })

  protected readonly maxNewRows = computed((): number =>
    this.relationshipType() === 'parent' ? 1 : 9999,
  )

  protected readonly groupBy = computed(
    (): EditableRowField<EntityRelationship> | undefined =>
      this.relationshipType() === 'parent' ? undefined : 'child_entity_uid',
  )

  private relatedEntityOptionTerminationDateColumn = computed((): ColDef => ({
    field: 'child_entity_termination_date',
    headerName: `Existing ${getDetailsFieldLabel('termination_date')}`,
    width: 160,
    valueGetter: ({ data }) => {
      const childEntityUid = data?.child_entity_uid
      if (childEntityUid) {
        const option = this.relatedEntityOptionsLookup()[childEntityUid]
        if (option) {
          return (option as ChildEntityOption).termination_date
        }
      }
      return undefined
    },
    valueFormatter: dateOnlyFormatter,
  }))

  protected readonly columns = computed((): readonly ColDef[] => {
    const cellEditorParams: SelectCellEditorParams<EntityRelationship> = {  options: this.relatedEntityOptions() }
    return [
      {
        field: this.relationshipType() === 'parent' ? 'parent_entity_uid' : 'child_entity_uid',
        headerName: this.entityTypeMetadata().entity_type_name,
        width: 300,
        editable: true,
        cellEditor: SelectCellEditorComponent,
        cellEditorParams,
        valueFormatter: getOptionsFormatter(this.relatedEntityOptionsLookup()),
      },
      ...this.relationshipType() === 'child' ? [this.relatedEntityOptionTerminationDateColumn()] : [],
      ...Object.values(effectiveDateColumns),
      ...Object.values(auditColumns),
    ]
  })

  protected readonly editorSpec = computed((): EditorSpec<EntityRelationship> => ({
    columns: this.columns(),
    getId,
    initNewRow: () => {
      // generate a unique id for the new row - will be replaced on save
      return { entity_relationship_uid: uuidV4() }
    },
    validate: rows =>
      validateRelationships(rows, getId, this.relationshipType(), this.relatedEntityOptionsLookup()),
    save: rows => this.entityManagementService.updateRelationships(
      this.entity(), this.entityTypeMetadata().entity_type_code, this.relationshipType(), rows,
    ),
  }))

  constructor() {
    effect(() => {
      const entityTypeCode = this.entityTypeMetadata().entity_type_code
      const optionsObservable = this.relationshipType() === 'parent'
        ? this.getParentEntityOptions(entityTypeCode)
        : this.getChildEntityOptions(this.entity(), entityTypeCode)

      untracked(() => {
        this.relatedEntityOptions.set([])
        optionsObservable.subscribe({
          next: options => {
            this.relatedEntityOptions.set(sortedOptions(options))
          },
          error: e => {
            this.error.emit(e)
          },
        })
      })
    })
  }

  private getParentEntityOptions(entityTypeCode: EntityTypeCode): Observable<readonly Option[]> {
    return this.entityManagementService.getEntities(entityTypeCode).pipe(
      map(entities => entities.map(toOption)),
    )
  }

  private getChildEntityOptions(entity: Entity, entityTypeCode: EntityTypeCode): Observable<readonly ChildEntityOption[]> {
    return this.entityManagementService.getChildEntityCandidates(entity, entityTypeCode)
      .pipe(
        map(candidates => candidates.map(toChildEntityOption)),
      )
  }
}
